'use client';

import { useState, useEffect } from 'react';
import { X, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EmojiPickerModal } from './emoji-picker-modal';

interface EditProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: ProjectEditData) => void;
  project: {
    id: string;
    name: string;
    description?: string;
    deadline?: string;
    emoji?: string;
    type?: string;
  };
}

export interface ProjectEditData {
  name: string;
  description: string;
  deadline: string;
  collaborators: string[];
  emoji: string;
}

export function EditProjectDialog({ isOpen, onClose, onSave, project }: EditProjectDialogProps) {
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description || '');
  const [deadline, setDeadline] = useState(project.deadline || '');
  const [collaboratorSearch, setCollaboratorSearch] = useState('');
  const [selectedEmoji, setSelectedEmoji] = useState(project.emoji || '📁');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Reset form when project changes
  useEffect(() => {
    setName(project.name);
    setDescription(project.description || '');
    setDeadline(project.deadline || '');
    setSelectedEmoji(project.emoji || '📁');
  }, [project]);

  if (!isOpen) return null;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave({
        name,
        description,
        deadline,
        collaborators: [],
        emoji: selectedEmoji,
      });
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Dialog */}
      <div className="relative bg-white rounded-[14px] shadow-xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-medium text-gray-900">Edit Project</h2>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-[10px] hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Icon and Project Name */}
          <div className="flex items-start gap-4">
            {/* Icon Selector */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Icon
              </label>
              <button
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="w-14 h-14 bg-gray-100 rounded-[10px] flex items-center justify-center hover:bg-gray-200 transition-colors border border-gray-200"
              >
                <span className="text-4xl">{selectedEmoji}</span>
              </button>
              
              {/* Emoji Picker Modal rendered below */}
            </div>

            {/* Project Name */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter project name"
                className="w-full h-[42px] px-4 border border-gray-300 rounded-[10px] bg-white text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
              />
            </div>
          </div>

          {/* Project Context / Company Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project Context / Company Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Provide context about the project, company background, target audience, or business goals..."
              className="w-full h-[100px] p-4 border border-gray-300 rounded-[10px] bg-white text-base text-gray-900 placeholder:text-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
            />
          </div>

          {/* Deadline */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Deadline
            </label>
            <div className="relative">
              <input
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="w-full h-[42px] px-4 border border-gray-300 rounded-[10px] bg-white text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
              />
            </div>
          </div>

          {/* Collaborators */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Collaborators
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={collaboratorSearch}
                onChange={(e) => setCollaboratorSearch(e.target.value)}
                placeholder="Search and add collaborators..."
                className="w-full h-[42px] pl-10 pr-4 border border-gray-300 rounded-[10px] bg-white text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={onClose}
            className="text-base text-gray-700 hover:text-gray-900 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !name.trim()}
            className={cn(
              'h-10 px-6 rounded-[10px] font-medium transition-colors',
              name.trim() && !isSaving
                ? 'bg-brand-primary text-white hover:bg-brand-primary/90'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            )}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Emoji Picker Modal */}
      <EmojiPickerModal
        isOpen={showEmojiPicker}
        onClose={() => setShowEmojiPicker(false)}
        onSelect={(emoji) => setSelectedEmoji(emoji)}
        currentEmoji={selectedEmoji}
      />
    </div>
  );
}

